import { Scaling } from 'redux/mode/reducers';

export interface Size {
	width: number;
	height: number;
}

export interface Frame {
	width: number;
	height: number;
}

export interface Viewport {
	x: number;
	y: number;
	width: number;
	height: number;
	scale: number; // how much iframe content is scaled up/down
}

const MIN_FRAME_SPACING = 20;

// Helper to ensure a number is valid (not NaN or Infinity)
const ensureValidNumber = (value: number, defaultValue: number = 0): number => {
	return isNaN(value) || !isFinite(value) ? defaultValue : value;
};

const calculateRenderedDimensions = (
	effectiveWidth: number,
	frameHeight: number,
	scale: number
) => {
	return {
		renderedWidth: ensureValidNumber(effectiveWidth * scale, 0),
		renderedHeight: ensureValidNumber(frameHeight * scale, 0),
	};
};

const centerInContainer = (renderedWidth: number, renderedHeight: number, containerSize: Size) => {
	const x = Math.max(0, (containerSize.width - renderedWidth) / 2);
	const y = Math.max(0, (containerSize.height - renderedHeight) / 2);
	return {
		x: ensureValidNumber(x, 0),
		y: ensureValidNumber(y, 0),
	};
};

const calculateMockupScale = (
	frame: Frame,
	containerSize: Size,
	effectiveWidth: number
): number => {
	if (effectiveWidth === 0 || frame.height === 0) return 1.0;
	const scaleX = containerSize.width / effectiveWidth;
	const scaleY = containerSize.height / frame.height;
	const scale = Math.min(scaleX, scaleY);
	return ensureValidNumber(scale, 1.0);
};

// Main function
export const computeViewport = (
	frameSize: Size,
	containerSize: Size,
	scaling: Scaling,
	fullscreenMode: boolean = false
): Viewport => {
	// Guard against invalid inputs
	if (!frameSize || !containerSize || frameSize.width <= 0 || frameSize.height <= 0) {
		return {
			x: 0,
			y: 0,
			width: ensureValidNumber(frameSize?.width ?? 0, 0),
			height: ensureValidNumber(frameSize?.height ?? 0, 0),
			scale: 1.0,
		};
	}

	// Calculate scale based on scaling mode (same for both fullscreen and normal)
	const scale =
		scaling === 'mockup'
			? calculateMockupScale(
					{ width: frameSize.width, height: frameSize.height },
					containerSize,
					frameSize.width
			  )
			: 1.0;

	const safeScale = ensureValidNumber(scale, 1.0);

	//---[ fullscreen ]---------------------
	if (fullscreenMode) {
		if (scaling === 'mockup') {
			const width = containerSize.width / safeScale;
			const height = containerSize.height / safeScale;
			return {
				x: 0,
				y: 0,
				width: ensureValidNumber(width, containerSize.width),
				height: ensureValidNumber(height, containerSize.height),
				scale: safeScale,
			};
		} else {
			// `actual-size`
			return {
				x: 0,
				y: 0,
				width: ensureValidNumber(containerSize.width, 0),
				height: ensureValidNumber(containerSize.height, 0),
				scale: safeScale,
			};
		}
	}

	//---[ non-fullscreen ]---------------------
	const { renderedWidth, renderedHeight } = calculateRenderedDimensions(
		frameSize.width,
		frameSize.height,
		safeScale
	);
	const { x, y } = centerInContainer(renderedWidth, renderedHeight, containerSize);
	if (scaling === 'mockup') {
		return {
			x: ensureValidNumber(x, 0),
			y: ensureValidNumber(y, 0),
			width: ensureValidNumber(frameSize.width, 0),
			height: ensureValidNumber(frameSize.height, 0),
			scale: safeScale,
		};
	}
	// `actual-size`
	return {
		x: ensureValidNumber(x, 0),
		y: ensureValidNumber(y, 0),
		width: ensureValidNumber(frameSize.width, 0),
		height: ensureValidNumber(frameSize.height, 0),
		scale: safeScale,
	};
};
